import { faBan, faCheck, faEdit, faGlasses, faTable } from '@fortawesome/free-solid-svg-icons';

import { Column, DataTable, DataTableContainer, Filter, TableActionButton } from 'components/DataTable';

import { useModal } from 'hooks/useModal';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
    SpaceBookingCancellationForm,
    SpaceBookingDatesComponent,
    SpaceBookingPreview,
    SpaceBookingPreviewComponent
} from '.';
import { useApi } from '../../../hooks/useApi';

import { Action, ActionCallbackProps } from 'components/DataTable/DataTable';
import useFileDownload from 'hooks/useFileDownload';
import { SpaceBooking } from '../types';
import { SpaceBookingEditForm } from './SpaceBookingEditForm';

interface Props {
    clubSlug: string;
}

export const RequestedSpaceBookingList = ({ clubSlug }: Props) => {
    const [isLoading, setIsLoading] = useState(false);
    const [setModal, resetModal] = useModal();
    const asyncList = useRef<any>(undefined);
    const api = useApi();
    const download = useFileDownload();

    const handleDownloadCSV = useCallback(
        ({ selection, filter }: ActionCallbackProps) => {
            setIsLoading(true);
            api({
                url: `space/booking/${clubSlug}/csv`,
                method: 'POST',
                data: { selection, filter: { ...filter, status: { value: 0 } } },
                onBefore: () => setIsLoading(false),
                onSuccess: (data) => download(data.download_id)
            });
        },
        [download]
    );

    const handleShowSpaceBookingPreview = useCallback(
        (booking: SpaceBooking) =>
            setModal({
                title: `Buchung von "${booking.user_name}"`,
                content: <SpaceBookingPreview booking={booking} />,
                show: true
            }),
        []
    );

    const handleConfirmBooking = useCallback((booking: SpaceBooking) => {
        api({
            url: `space/booking/${booking.id}/confirm`,
            onSuccess: () => asyncList.current?.reload()
        }).catch(() => {});
    }, []);

    const handleCancelSpaceBooking = useCallback(
        (booking: SpaceBooking) =>
            setModal({
                title: `Buchung von "${booking.user_name}"`,
                content: (
                    <SpaceBookingCancellationForm
                        booking={booking}
                        callback={() => {
                            asyncList.current?.reload();
                            resetModal();
                        }}
                    />
                ),
                show: true
            }),
        []
    );

    const handleEditSpaceBooking = useCallback(
        (booking: SpaceBooking) =>
            setModal({
                title: `Buchung von "${booking.user_name}" ändern`,
                content: (
                    <SpaceBookingEditForm
                        booking={booking}
                        clubSlug={clubSlug}
                        adminMode
                        callback={() => {
                            asyncList.current?.reload();
                            resetModal();
                        }}
                    />
                ),
                show: true,
                size: 70
            }),
        []
    );

    const columns: Column<SpaceBooking>[] = [
        {
            uid: 'user_name',
            name: 'Benutzer',
            renderer(_, item?) {
                return <SpaceBookingPreviewComponent booking={item} />;
            }
        },
        {
            uid: 'start',
            name: 'Daten',
            allowsSorting: true,
            renderer(_, item?) {
                return <SpaceBookingDatesComponent booking={item} />;
            }
        },
        {
            uid: 'spaces',
            name: 'Plätze',
            renderer(value) {
                return (
                    <ul className="tw-m-0 tw-list-none tw-p-0">
                        {value?.map((space) => (
                            <li key={space.id}>
                                <p
                                    className={`tw-m-0 tw-p-0 tw-font-extralight ${value.length > 1 ? 'font-size-[12px]' : ''}`}
                                >
                                    {space.name}
                                </p>
                            </li>
                        ))}
                    </ul>
                );
            }
        },
        {
            uid: 'price',
            name: 'Preis',
            renderer(value) {
                return value !== null ? `${value} €` : '';
            }
        },
        {
            uid: 'actions',
            name: 'Optionen',
            align: 'end'
        }
    ];

    const filters: Filter[] = [
        {
            filterName: 'Nummern',
            filterKey: 'ids',
            filterPlaceholder: 'Trenne mehrere Nummern mit Komma um diese gleichzeitig anzuzeigen',
            filterType: 'text'
        },
        { filterName: 'Plätze', filterKey: 'spaces', filterType: 'text' },
        { filterName: 'Startdatum', filterKey: 'start', filterType: 'date' },
        { filterName: 'Storniert am', filterKey: 'cancelled_at', filterType: 'date' },
        { filterName: 'Gebucht am', filterKey: 'created_at', filterType: 'date' },
        { filterName: 'Preis', filterKey: 'price', filterType: 'numeric' },
        { filterName: 'Benutzer E-Mail', filterKey: 'email', filterType: 'text' },
        { filterName: 'Platzabo', filterKey: 'subscriptions', filterType: 'text' }
    ];

    const actions = useMemo(
        (): Action[] => [{ key: 'csv', label: 'Als CSV exportieren', icon: faTable, action: handleDownloadCSV }],
        [handleDownloadCSV]
    );

    return (
        <DataTableContainer>
            <DataTable
                tableProps={{
                    sticked: true,
                    bordered: true,
                    borderWeight: 'light',
                    compact: false,
                    selectionMode: 'multiple'
                }}
                actions={actions}
                actionsLoading={isLoading}
                asyncListRef={asyncList}
                columns={columns}
                filters={filters}
                fetchUrl={`space/booking/${clubSlug}/requested`}
                rowActions={(item) => (
                    <TableActionButton.Container justify="end">
                        <TableActionButton icon={faGlasses} onClick={() => handleShowSpaceBookingPreview(item)} />
                        <TableActionButton icon={faEdit} onClick={() => handleEditSpaceBooking(item)} />
                        <TableActionButton icon={faCheck} onClick={() => handleConfirmBooking(item)} />
                        {!item.cancelled_at && (
                            <TableActionButton icon={faBan} onClick={() => handleCancelSpaceBooking(item)} />
                        )}
                    </TableActionButton.Container>
                )}
            />
        </DataTableContainer>
    );
};

export default RequestedSpaceBookingList;
